import Image from 'next/image';
import { ButtonNew } from 'root/src/client/v2/common/components/form/ButtonNew';
import { GlowLine } from 'root/src/client/v2/common/components/GlowLine';
import { Cup, CupSize, CupVariant, } from 'root/src/client/web/giveaway/components/Cup';
import { useGiveawaySection } from 'root/src/client/web/giveaway/features/GiveawaySection/hooks/useGiveawaySection';
import moment from 'moment-mini';
import { BaseTooltip, BaseTooltipContent } from 'root/src/client/v2/common/components/BaseTooltip/BaseTooltip';
import { useCreatorLastGiveaway, useCreatorResolvedGiveaway, useIsCreatorGiveawaysLoading, useUserVouchers, } from 'root/src/client/web/giveaway/hooks/useCreatorGiveaways';
import LoadingCircles from 'root/src/client/v2/common/components/LoadingCircles';
import * as Carousel from 'root/src/client/v2/common/components/Carousel/Carousel';
import { GiveawayStatus, VoucherStatus } from '@daredrop-com/rewards-api-contract';
import Autoplay from 'embla-carousel-autoplay';
export const RewardWinners = () => {
    return (<div className="flex flex-initial flex-col bg-brown p-5 gap-8 border">
			<RewardWinnerHeader />
			<div className="flex bg-black">
				<RewardWinnersDetails />
			</div>
		</div>);
};
const RewardWinnerHeader = () => {
    const userVouchers = useUserVouchers();
    const resolvedGiveaway = useCreatorResolvedGiveaway();
    const isSingleWinner = !userVouchers.length
        && (resolvedGiveaway === null || resolvedGiveaway === void 0 ? void 0 : resolvedGiveaway.vouchers)
        && resolvedGiveaway.vouchers.length === 1;
    return (<div className="flex justify-between gap-8 items-center">
			<div className="flex flex-col items-end gap-1.5">
				<HeaderGlowLines />
			</div>
			<h1 className="font-bold uppercase text-center">Reward Winner{isSingleWinner ? '' : 's'}</h1>
			<div className="flex flex-col gap-1.5">
				<HeaderGlowLines />
			</div>
		</div>);
};
const HeaderGlowLines = () => {
    return (<>
			<GlowLine className="w-full" variant="purple"/>
			<GlowLine className="w-4/5" variant="green"/>
			<GlowLine className="w-3/5" variant="red"/>
		</>);
};
const RewardWinnersDetails = () => {
    const isLoading = useIsCreatorGiveawaysLoading();
    const lastGiveaway = useCreatorLastGiveaway();
    const vouchers = useUserVouchers();
    const availableVouchers = vouchers.filter(voucher => voucher.status === VoucherStatus.Unused);
    if (availableVouchers.length) {
        return (<div className="bg-black w-full flex">
				<RecentWinner voucher={availableVouchers[0]} isClaimReward/>
			</div>);
    }
    if (isLoading) {
        return <LoadingCircles width={50} height={50}/>;
    }
    if (!lastGiveaway) {
        return <NoGiveawayCreated label="No Giveaways Created"/>;
    }
    if (lastGiveaway.status === GiveawayStatus.Active) {
        return <GiveawayInProgress />;
    }
    if (lastGiveaway.status === GiveawayStatus.Stopped) {
        return <NoGiveawayCreated label="Creator canceled the last Giveaway"/>;
    }
    return (<div className="bg-black w-full flex">
			<RecentWinnersSlider resolvedGiveaway={lastGiveaway}/>
		</div>);
};
const NoGiveawayCreated = ({ label }) => {
    return (<div className="flex flex-col justify-center items-center text-sm py-6 gap-1">
			<Cup size={CupSize.SMALL} variant={CupVariant.Silver}/>
			<span className="text-center text-pink">{label}</span>
		</div>);
};
const GiveawayInProgress = () => {
    return (<div className="flex flex-col justify-center items-center text-sm py-6 gap-1">
			<Cup size={CupSize.SMALL} variant={CupVariant.Silver}/>
			<span className="text-center text-light-4">Giveaway in progress</span>
			<span className="text-center m-2">Please check back to see who the winner is.</span>
		</div>);
};
const RecentWinnersSlider = ({ resolvedGiveaway }) => {
    var _a;
    if (!((_a = resolvedGiveaway.vouchers) === null || _a === void 0 ? void 0 : _a.length)) {
        return <RecentWinner />;
    }
    if (resolvedGiveaway.vouchers.length === 1) {
        return <RecentWinner voucher={resolvedGiveaway.vouchers[0]}/>;
    }
    return (<Carousel.Root loop plugins={[Autoplay({
                delay: 5000,
                stopOnInteraction: false,
            })]}>
			<Carousel.Container className="no-scrollbar w-full flex">
				{resolvedGiveaway.vouchers.map(voucher => (<RecentWinner key={voucher.id} voucher={voucher}/>))}
			</Carousel.Container>
		</Carousel.Root>);
};
const RecentWinner = ({ voucher, isClaimReward }) => {
    var _a;
    return (<Carousel.Slide width="100%">
			<div className="flex flex-col sm:flex-row h-full items-center px-4 py-1 gap-2">
				<RecentWinnerInfo winnerName={(_a = voucher === null || voucher === void 0 ? void 0 : voucher.owner) === null || _a === void 0 ? void 0 : _a.name}/>
				<div className="flex-initial sm:min-h-[75%] sm:w-1 h-1 w-full rounded bg-light-4"/>
				<div className="flex flex-col items-start px-8 gap-2 flex-grow">
					<RecentWinnerReward voucher={voucher} isClaimReward={isClaimReward}/>
				</div>
			</div>
		</Carousel.Slide>);
};
const RecentWinnerInfo = ({ winnerName }) => {
    var _a, _b;
    const resolvedGiveaway = useCreatorResolvedGiveaway();
    return (<div className="flex min-w-[40%] flex-col justify-center items-center gap-2 p-6">
			<div className="flex gap-4 justify-center items-center">
				<Cup size={CupSize.SMALL} variant={CupVariant.Gold}/>
				<span className="flex text-light-4 text-base">Winner</span>
				<Cup size={CupSize.SMALL} variant={CupVariant.Gold}/>
			</div>
			<span className="font-mono font-bold text-lg motion-safe:animate-scale-down">{winnerName !== null && winnerName !== void 0 ? winnerName : '---'}</span>
			<div className="flex gap-2 text-sm">
				<span className="text-light-4">When:</span>
				<span title={new Date((_a = resolvedGiveaway === null || resolvedGiveaway === void 0 ? void 0 : resolvedGiveaway.endDate) !== null && _a !== void 0 ? _a : Date.now()).toLocaleTimeString()}>{moment((_b = resolvedGiveaway === null || resolvedGiveaway === void 0 ? void 0 : resolvedGiveaway.endDate) !== null && _b !== void 0 ? _b : Date.now()).format('MM/DD/YYYY')}</span>
			</div>
		</div>);
};
const RecentWinnerReward = ({ voucher, isClaimReward }) => {
    if (isClaimReward) {
        return <ClaimReward />;
    }
    if ((voucher === null || voucher === void 0 ? void 0 : voucher.status) === VoucherStatus.Expired) {
        return <NoAwardClaimed label="Award has expired"/>;
    }
    if ((voucher === null || voucher === void 0 ? void 0 : voucher.status) === VoucherStatus.Used) {
        return <AwardClaimed voucher={voucher}/>;
    }
    return <NoAwardClaimed label="No award claimed yet"/>;
};
const AwardClaimed = ({ voucher }) => {
    var _a, _b, _c, _d, _e, _f;
    return (<div className="flex flex-col gap-2 pb-4 pt-2 items-center sm:items-start">
			<span className="font-mono text-xl font-bold">{(_b = (_a = voucher.reward) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : 'Unknown'}</span>
			<div className="flex-col items-center lg:flex-row flex justify-center gap-4">
				<Image width={100} height={100} className="rounded !border-2 !border-solid !border-teal" src={(_d = (_c = voucher.reward) === null || _c === void 0 ? void 0 : _c.imageUrl) !== null && _d !== void 0 ? _d : ''} alt="Game image"/>
				<div className="flex flex-col items-start py-2 gap-2 text-left">
					<span className="text-light-4">Reward Claimed:</span>
					<span>{(_f = (_e = voucher.reward) === null || _e === void 0 ? void 0 : _e.name) !== null && _f !== void 0 ? _f : 'Unknown'} key</span>
				</div>
			</div>
		</div>);
};
const NoAwardClaimed = ({ label }) => {
    return (<div className="flex flex-col items-start gap-2 text-sm">
			<span className="text-light-4">Reward Claimed:</span>
			<span className="text-base">{label}</span>
		</div>);
};
const ClaimReward = () => {
    const { openRedeemVouchersModal, hasRewards } = useGiveawaySection();
    return (<div className="flex flex-col items-start py-2 gap-2 text-sm text-left">
			<span className="text-light-4">Reward Claimed:</span>
			<BaseTooltip tooltip={!hasRewards && <BaseTooltipContent>Currently there are no rewards to claim</BaseTooltipContent>}>
				<ButtonNew disabled={!hasRewards} onClick={openRedeemVouchersModal} className="sm:px-14 py-2" type="button">
					<span className="uppercase">
						Claim reward
					</span>
				</ButtonNew>
			</BaseTooltip>
		</div>);
};
